import React, { useState, useMemo } from "react";
import { X, Search, Ruler, Info } from "lucide-react";
import { ORBITEC_HEADS as HEADS, ORBITEC_ROWS as ROWS } from "@/data/orbitecElectrodeData";

const TR = {
  sk: {
    title: "Nastavenia wolfrámovej elektródy pre hlavy Orbitec",
    subtitle: "Dĺžky wolfrámovej elektródy podľa priemeru rúry a modelu orbitálnej hlavy",
    pipe: "Priemer rúry",
    search: "Hľadaj priemer (mm alebo palce)…",
    empty: "Žiadne výsledky",
    source: "Zdroj: oficiálna servisná príručka Orbitec — Längentabelle Wolframelektroden. Overuj podľa originálneho manuálu tvojej hlavy.",
    inch: "palce",
    close: "Zavrieť",
  },
  en: { title: "Tungsten electrode settings for Orbitec heads", subtitle: "Electrode length by pipe diameter and orbital head model", pipe: "Pipe diameter", search: "Search diameter (mm or inch)…", empty: "No results", source: "Source: official Orbitec service manual — Längentabelle Wolframelektroden. Always verify against your head's original documentation.", inch: "inch", close: "Close" },
  cs: { title: "Nastavení wolframové elektrody pro hlavy Orbitec", subtitle: "Délky wolframové elektrody podle průměru trubky a modelu orbitální hlavy", pipe: "Průměr trubky", search: "Hledej průměr (mm nebo palce)…", empty: "Žádné výsledky", source: "Zdroj: oficiální servisní manuál Orbitec — Längentabelle Wolframelektroden. Ověřuj podle originálního manuálu tvé hlavy.", inch: "palce", close: "Zavřít" },
  de: { title: "Wolframelektroden-Einstellung für Orbitec-Köpfe", subtitle: "Elektrodenlänge nach Rohrdurchmesser und Orbitalkopf-Modell", pipe: "Rohrdurchmesser", search: "Durchmesser suchen (mm oder Zoll)…", empty: "Keine Ergebnisse", source: "Quelle: offizielles Orbitec Servicehandbuch — Längentabelle Wolframelektroden. Immer gegen die Originaldokumentation deines Kopfes prüfen.", inch: "Zoll", close: "Schließen" },
  pl: { title: "Ustawienia elektrody wolframowej dla głowic Orbitec", subtitle: "Długość elektrody wg średnicy rury i modelu głowicy orbitalnej", pipe: "Średnica rury", search: "Szukaj średnicy (mm lub cale)…", empty: "Brak wyników", source: "Źródło: oficjalny podręcznik serwisowy Orbitec — Längentabelle Wolframelektroden. Zawsze weryfikuj z oryginalną dokumentacją głowicy.", inch: "cale", close: "Zamknij" },
  fr: {
    title: "Réglages de l'électrode tungstène pour têtes Orbitec",
    subtitle: "Longueurs d'électrode tungstène selon le diamètre du tube et le modèle de tête orbitale",
    pipe: "Diamètre du tube",
    search: "Chercher diamètre (mm ou pouces)…",
    empty: "Aucun résultat",
    source: "Source : manuel de service officiel Orbitec — Längentabelle Wolframelektroden. Vérifie toujours selon le manuel original de ta tête.",
    inch: "pouces",
    close: "Fermer",
  },
};

/**
 * Full-screen luxury modal showing the Orbitec tungsten electrode length table.
 * Props:
 *   open: boolean — whether the modal is visible
 *   onClose: () => void — called when user dismisses (X, backdrop, ESC)
 *   lang: current UI language
 */
export default function OrbitecElectrodeTable({ open, onClose, lang = "sk" }) {
  const tr = TR[lang] || TR.sk;
  const [q, setQ] = useState("");

  // ESC key closes the modal — small UX polish, no library needed.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while modal is open (mobile safe).
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ROWS;
    const n = parseFloat(s.replace(",", "."));
    if (!isNaN(n)) {
      // Numeric search: match by pipe_mm or pipe_inch tolerating small typos.
      return ROWS.filter((r) => Math.abs(r.pipe_mm - n) < 0.5 || Math.abs(r.pipe_inch - n) < 0.05);
    }
    return ROWS.filter((r) => `${r.pipe_mm} ${r.pipe_inch}`.includes(s));
  }, [q]);

  if (!open) return null;

  return (
    <div
      data-testid="orbitec-electrode-modal"
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-6xl bg-[var(--wb-surface)] border border-gold-800/40 shadow-[0_0_60px_rgba(212,175,55,0.15)] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-zinc-800 bg-gradient-to-r from-gold-950/20 to-transparent">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Ruler className="w-4 h-4 text-gold-400" />
              <span className="text-[10px] font-mono uppercase tracking-[3px] text-gold-400">Orbitec</span>
            </div>
            <h2 className="font-display font-bold text-lg sm:text-2xl text-white leading-tight">{tr.title}</h2>
            <p className="text-xs text-zinc-400 mt-1.5">{tr.subtitle}</p>
          </div>
          <button
            data-testid="orbitec-electrode-close"
            onClick={onClose}
            className="flex-shrink-0 w-10 h-10 border border-zinc-800 hover:border-gold-700 hover:text-gold-400 flex items-center justify-center text-zinc-400 transition-colors"
            aria-label={tr.close}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search bar */}
        <div className="p-4 sm:p-5 border-b border-zinc-800 bg-zinc-950/50">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="orbitec-electrode-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr.search}
              className="wb-input w-full pl-10 pr-3 py-2 text-sm font-mono"
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm font-mono">{tr.empty}</div>
          ) : (
            <table className="w-full text-xs font-mono" data-testid="orbitec-electrode-table">
              <thead className="sticky top-0 bg-zinc-950 z-10">
                <tr className="border-b border-gold-800/40">
                  <th className="text-left px-3 py-3 text-gold-400 uppercase text-[10px] tracking-widest whitespace-nowrap" colSpan={2}>
                    {tr.pipe} Ø
                  </th>
                  {HEADS.map((h) => (
                    <th key={h.key} className="text-center px-2 py-3 text-gold-400 uppercase text-[10px] tracking-widest whitespace-nowrap" colSpan={2}>
                      {h.label}
                    </th>
                  ))}
                </tr>
                <tr className="border-b border-zinc-800 text-[9px] text-zinc-500 uppercase tracking-wider">
                  <th className="text-right px-3 py-2 font-normal">mm</th>
                  <th className="text-right px-3 py-2 font-normal border-r border-zinc-800">{tr.inch}</th>
                  {HEADS.map((h) => (
                    <React.Fragment key={h.key}>
                      <th className="text-right px-2 py-2 font-normal">mm</th>
                      <th className="text-right px-2 py-2 font-normal border-r border-zinc-800/60 last:border-r-0">{tr.inch}</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={i}
                    data-testid={`orbitec-electrode-row-${r.pipe_mm}`}
                    className={`border-b border-zinc-900 ${i % 2 === 0 ? "bg-zinc-950/40" : ""} hover:bg-gold-950/20 transition-colors`}
                  >
                    <td className="text-right px-3 py-2 text-white font-semibold whitespace-nowrap">{r.pipe_mm.toFixed(2)}</td>
                    <td className="text-right px-3 py-2 text-zinc-400 border-r border-zinc-800 whitespace-nowrap">{r.pipe_inch.toFixed(3)}</td>
                    {HEADS.map((h) => {
                      const v = r[h.key];
                      if (!v) {
                        return (
                          <React.Fragment key={h.key}>
                            <td className="text-right px-2 py-2 text-zinc-700">—</td>
                            <td className="text-right px-2 py-2 text-zinc-700 border-r border-zinc-800/60 last:border-r-0">—</td>
                          </React.Fragment>
                        );
                      }
                      return (
                        <React.Fragment key={h.key}>
                          <td className="text-right px-2 py-2 text-gold-300 font-semibold">{v[0]}</td>
                          <td className="text-right px-2 py-2 text-zinc-400 border-r border-zinc-800/60 last:border-r-0">{v[1].toFixed(3)}</td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer / source disclaimer */}
        <div className="border-t border-zinc-800 p-3 sm:p-4 bg-zinc-950/60">
          <div className="flex items-start gap-2 text-[10px] text-zinc-500 leading-relaxed">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-gold-500" />
            <span>{tr.source}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
